import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { PaymentTotal } from '../../component/PaymentTotal/PaymentTotal';
import { useCompany } from '../../component/CompanySelection/CompanyContext';
import PaymentFilter, { defaultFilter } from '../../component/PaymentFilter/PaymentFilter';
import PaymentTableWrapper from '../../component/PaymentTable/PaymentTableWrapper';
import { makeRequest, RequestParameters, cancelRequest } from '../../service/PaymentSearchService';
import { SINGLE_PARTY } from '../../config/WorkflowTypes';
import { VIRTUAL_CARD } from '../../config/PaymentMethods';
import { PaymentMethodFilterTypes } from '../../config/FilterOptionTypes';
import Has from '../../component/Permission/Has';
import { FF_INDIVIDUAL_PAYMENTS } from '../../config/FeatureFlagTypes';

export default function IndividualPayments(props) {
    //This screen omits the Virtual Credit Card payment method
    const paymentMethods = PaymentMethodFilterTypes.filter(m => m.value !== VIRTUAL_CARD);

    const location = useLocation();
    const { companyNumber } = useCompany();
    const workflow = SINGLE_PARTY;
    const initialFilterState = { ...defaultFilter(), companyNumber, workflow, ...location?.state?.search };
    const [paymentOptions, setPaymentOptions] = useState(initialFilterState);
    const [exportOptions, setExportOptions] = useState(initialFilterState);
    const [paymentData, setPaymentData] = useState({ disbursements: [], totalDisbursementAmount: 0.00 });
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [previousOptions, setPreviousOptions] = useState({
        previousFilter: initialFilterState,
        previousPayments: paymentData,
        previousExport: initialFilterState,
        previousTableFilter: {
            sortColumn: "DATE",
            sortDirection: "desc",
        }
    });

    const updatePaymentData = (tableSort, filterOptions) => {
        setIsLoading(true);
        // This clear is to fix repeated table column filter cancel resets
        if (paymentData?.tableFilters) {
            setPaymentData({ ...paymentData, tableFilters: undefined });
        }
        setPreviousOptions({ ...previousOptions, previousPayments: paymentData });
        // tableSort Structure {column: columnType, direction: direction}
        let request = RequestParameters({}, filterOptions ? filterOptions : paymentOptions);

        if (tableSort) {
            let { column: sortColumn, direction: sortDirection } = tableSort;
            request = RequestParameters(request, { sortColumn, sortDirection });
        }
        setPaymentOptions(request);
        makeRequest(request, companyNumber, dataHandler, errorHandler, setExportOptions);
    }

    const cancel = () => {
        cancelRequest();
        setPaymentData({
            ...previousOptions.previousPayments,
            tableFilters: previousOptions.previousTableFilter,
        });
        setPaymentOptions(previousOptions.previousFilter);
        setExportOptions(previousOptions.previousExport);
        setIsLoading(false);
    }

    const dataHandler = (data, exportRequest, paymentOptions) => {
        setPreviousOptions({
            ...previousOptions,
            previousFilter: paymentOptions,
            previousExport: exportRequest,
            previousPayments: data,
            previousTableFilter: {
                sortColumn: paymentOptions.sortColumn,
                sortDirection: paymentOptions.sortDirection,
            }
        })
        setPaymentData(data);
        setHasError(false);
        setIsLoading(false);
    }

    const errorHandler = (error) => {
        if (error && error.response.status !== 200) {
            setHasError(true);
            setPaymentData({ disbursements: [], totalDisbursementAmount: 0.00 });
        } else {
            setHasError(false);
        }
        setIsLoading(false);
    }

    const handleFilterApply = (filters) => {
        updatePaymentData(null, filters);
    }

    useEffect(() => {
        updatePaymentData();
    }, []);     // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <React.Fragment>
            <Has isPageLevel={true} featureFlag={FF_INDIVIDUAL_PAYMENTS}>
                {/* <!-- Toggle Button / Mobile Header--> */}

                {/* <!-- Page content--> */}
                <div className="container-fluid" data-testid="individual-payments-page">

                    <div className="cb-row page-title-area">
                        <div className="cb-col-xs-8 cb-col-lg-8 page-title">
                            <h1 data-testid="individual-payments-page-header-title">Individual Payments</h1>
                        </div>
                        <div className="cb-col-xs-4 cb-col-lg-4">
                            {/*Issue Ad Hoc Payment buttons have been removed until the functionality is implemented */}
                            {/* <!--Use on Desktop--> */}
                            {/* <button type="button" hidden className="cb-btn title-button d-none d-lg-block" data-testid="individual-payments-page-header-adhoc-button">+ Issue Ad Hoc Payment</button> */}
                            {/* <!--Use on Mobile--> */}
                            {/* <button type="button" hidden className="m-titleButton d-lg-none">+ Issue Ad Hoc Payment</button> */}
                        </div>
                    </div>

                    <PaymentTotal total={paymentData.totalDisbursementAmount} maxResults={paymentData.maxResults} />

                    <PaymentFilter
                        paymentMethods={paymentMethods}
                        options={paymentOptions}
                        onApplyFilter={handleFilterApply}
                        onCancelFilter={() => { setPaymentOptions({ ...defaultFilter(), companyNumber, workflow }); }}
                    />

                    {hasError ?
                        <div data-testid="payment-data-error">
                            <h3 className="text-center">An error occurred getting payment data.</h3>
                        </div>
                        :
                        <PaymentTableWrapper
                            paymentTableType="individual"
                            paymentData={paymentData}
                            updatePaymentData={updatePaymentData}
                            isLoading={isLoading}
                            cancel={cancel}
                            exportOptions={exportOptions} />
                    }
                </div>
            </Has>
        </React.Fragment>
    );
}